import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { LoggedInUserService } from '@formbird/services';

/**
 * Sends anonymous and public-account visitors to `config.publicDefaultURL`, while leaving standard
 * Formbird navigation untouched for everyone else.
 *
 * Only active when `config.publicDefaultURL` is set in the client config — deployments that don't
 * configure it are unaffected and get the route's own `data.defaultRoute` (or `/default` if unset),
 * exactly as a static `redirectTo` would have.
 *
 * Reused on both the root path and the catch-all wildcard route; the destination for a `normal`
 * (non-public) session is customized per route via `route.data.defaultRoute` (`/default` for root,
 * `/notFound` for the wildcard).
 *
 * This only decides *where the address bar points*. Silently authenticating an anonymous visitor into
 * a public account (so that `publicDefaultURL` doesn't just bounce them straight back to a login page)
 * is a separate, route-specific concern left to whichever deployment configures it.
 */
@Injectable({
  providedIn: 'root',
})
export class PublicDefaultUrlRedirectGuard implements CanActivate {
  private router = inject(Router);
  private loggedInUserService = inject(LoggedInUserService);

  /**
   * @param route - the matched route snapshot; `route.data.defaultRoute` selects where a
   *                normal session (or any session, when no public default URL is configured) is sent
   * @returns a UrlTree redirecting to `config.publicDefaultURL` or the route's default
   */
  canActivate(route: ActivatedRouteSnapshot): UrlTree {
    const defaultRoute = route.data?.defaultRoute || '/default';
    const config = (<any>window).ftClientConfig;
    const publicDefaultURL = config?.publicDefaultURL;

    if (!publicDefaultURL) {
      return this.router.createUrlTree([defaultRoute]);
    }

    const sessionState = this.loggedInUserService.getSessionState();

    return this.router.createUrlTree([sessionState === 'normal' ? defaultRoute : publicDefaultURL]);
  }
}
